import { Role } from '@agingplan/src';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { environment } from 'src/environments/environment';
import { PageFragment } from '../../our-data/page-fragment';
import { SidebarRoute } from './sidebar';
@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  /**
   * The notification count that is displayed on the sidebar
   */
  public notifications$ = new BehaviorSubject<SidebarNotifications | undefined>(undefined);

  /**
   * The routes that are displayed on the sidebar
   */
  public routes$ = new BehaviorSubject<SidebarRoute[]>([]);

  /**
   * The active_route which is active
   */
  public active_route$ = new BehaviorSubject<string>('');

  /**
   * Whether or not the sidebar is collapsed
   */
  public collapsed$ = new BehaviorSubject<boolean>(false);

  /**
   * allow sidebar to collapsed
   */
  public allow_collapsed$ = new BehaviorSubject<boolean>(false);

  /**
   * Permitted Paths for user
   */
  public permitted_paths = new BehaviorSubject<string[]>([]);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authenticationService: AuthenticationService,
  ) {
    this.notifications$.subscribe((notifications) => {
      if (notifications) {
        this.getNotificationCount(notifications);
      }
    });
  }

  /**
   * Get the sidebar route from a given fragment.
   * @param {PageFragment} path - The fragment to get the route for.
   * @returns {SidebarRoute | undefined} - The route that contains the given fragment.
   */
  public getSidebarRouteFromPageFragmentPage(page: PageFragment): SidebarRoute | undefined {
    const routes = this.routes$.value;

    for (const route of routes) {
      if (this.getSidebarRouteFromPageFragmentPageHelper(route, page)) {
        return route;
      }
    }

    return undefined;
  }

  /**
   * Recursively check if a given route contains a descendant with the given fragment.
   * @param {SidebarRoute} route - The route to check.
   * @param {PageFragment} path - The fragment to check for.
   * @returns {boolean} - Whether the route contains a descendant with the given fragment.
   */
  private getSidebarRouteFromPageFragmentPageHelper(route: SidebarRoute, path: PageFragment): boolean {
    if (route.title.toLowerCase() === path.page.toLowerCase()) {
      return true;
    }
    if (route.children) {
      for (const child of route.children) {
        if (this.getSidebarRouteFromPageFragmentPageHelper(child, path)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Ensure a given route contains a descendant with the given fragment. Will navigate to the first descendant if it does not.
   * @param {PageFragment} page - The fragment to check for.
   * @returns {Promise<boolean>} - Whether the route contains a descendant with the given fragment.
   */
  public async ensureSidebarRouteContainsDescendantFragment(page: PageFragment) {
    const fragment = this.activatedRoute.snapshot.fragment;
    const route = this.getSidebarRouteFromPageFragmentPage(page);

    if (route) {
      if (route.children) {
        for (const child of route.children) {
          // Check if the child has children
          if (child.children) {
            // Check if a grandchild has the fragment
            for (const grandchild of child.children) {
              if (grandchild.fragment === fragment) {
                return true;
              }
            }
          }
          // Check if the child has the fragment
          if (child.fragment === fragment) {
            return true;
          }
        }
        // Redirect to the first child by default
        return await this.router.navigate([route.path], { fragment: route.children[0].fragment });
      }
    }

    return false;
  }

  /**
   * Recursively get the count of all the notifications for a given route, its children, and set the count property of the route.
   * @param {SidebarNotifications} notifications - The side bar notifications.
   */
  private async getNotificationCount(notifications: SidebarNotifications) {
    const routes = this.routes$.value;
    for (const [key, value] of Object.entries(notifications)) {
      const route = routes.find((route) => {
        const normalizedKey = key.normalizePlural();
        const normalizedTitle = route.title.normalizePlural();
        return normalizedTitle.includes(normalizedKey);
      });

      if (route) {
        for (const [childKey, childValue] of Object.entries(value)) {
          const child = route.children?.find((child) => {
            const normalizedChildKey = childKey.normalizePlural();
            const normalizedChildFragment = child?.fragment?.normalizePlural();
            const normalizedChildTitle = child.title.normalizePlural();
            const titleWithoutSpaces = normalizedChildTitle.replace(/\s+/g, '');
            return normalizedChildFragment?.includes(normalizedChildKey) || normalizedChildTitle.includes(normalizedChildKey) || titleWithoutSpaces.includes(normalizedChildKey);
          });

          if (child) {
            if (typeof childValue === 'object' && childValue !== null) {
              for (const [grandchildKey, grandchildValue] of Object.entries(childValue)) {
                const grandchild = child.children?.find((grandchild) => {
                  const normalizedGrandchildKey = grandchildKey.normalizePlural();
                  const normalizedGrandchildFragment = grandchild.fragment?.normalizePlural();
                  const normalizedGrandchildTitle = grandchild.title.slug();
                  return (
                    normalizedGrandchildFragment?.includes(normalizedGrandchildKey) ||
                    (normalizedGrandchildTitle.includes(normalizedGrandchildKey) && normalizedGrandchildTitle.split('-').length === normalizedGrandchildKey.split('-').length)
                  );
                });

                if (grandchild) {
                  grandchild.count = grandchildValue;
                }
              }
              child.count = this.sumChildNotifications(child);
            } else {
              child.count = childValue;
            }
          }
        }
        route.count = this.sumChildNotifications(route);
      }
    }
    this.routes$.next(routes);
  }

  /**
   * Get the count of all notifications for a given route.
   * @param {SidebarRoute} route - The route to get the total count of notifications for.
   * @returns {number} The total count of notifications for a given route.
   */
  private sumChildNotifications(route: SidebarRoute): number {
    return route.children?.reduce((acc, child) => acc + (child.count || 0), 0) || 0;
  }

  /**
   * Update sidebar menu is collapsed to the observer
   * @param {boolean} collapsed - The sidebar menu is collapsed
   */
  public setSidebarState(collapsed: boolean) {
    this.collapsed$.next(collapsed);
  }

  /**
   * Get permitted paths for logged user
   */
  public getPermittedPaths() {
    const user = this.authenticationService.user.value;
    const permitted_paths = this.permitted_paths.value;
    if (user?.roles?.length) {
      user?.roles.forEach((role: Role) => {
        if(role?.guards?.length) {
          role?.guards.forEach((guard) => {
            if (!permitted_paths.includes(guard)) {
              permitted_paths.push(guard);
            }
          });
        }
      });
    }
    this.permitted_paths.next(permitted_paths);
  }

  /**
   * Recursively filters the routes based on whether the user has access to them.
   * @param {SidebarRoute} route - The route to filter.
   * @param {SidebarRoute | null} parent - The parent route, if any.
   * @returns A boolean indicating whether the route should be included or not.
   */
  private filterRoutes(route: SidebarRoute, parent: SidebarRoute | null = null): boolean {
    if (route.children) {
      // Filter children recursively
      route.children = route.children.filter((child) => this.filterRoutes(child, route));
      // Include the route if it has any children left after filtering
      return route.children.length > 0;
    }

    if (parent) {
      // Check if the user has permission for this child route
      return this.hasUrlpermission(route, parent);
    }

    // Include top-level routes by default
    return true;
  }

  /**
   * Filters the user-permitted routes based on whether the user has access to them.
   */
  public filterUserPermittedRoute() {
    let routes = this.routes$.value;
    routes = routes.filter((route) => this.filterRoutes(route));
    this.routes$.next(routes);
  }

  /**
   * Verify that the user has access to a specific URL.
   * paths are restricted if the user does not have access.
   * @param url - The URL to check access for.
   */
  public hasUrlpermission(child: SidebarRoute, parent: SidebarRoute): boolean {
    const parentPath = parent.path ?? '';
    const childFragmentOrPath = child.fragment ? `#${child.fragment}` : child.path ? `#${child.path}` : '';
    const url_path = `${parentPath}${childFragmentOrPath}`;
    const url = new URL(url_path, environment.web_url);
    const full_path = url.pathname + url.hash;
    const permitted_paths = this.permitted_paths.value;
    return permitted_paths.some((guard) => {
      return full_path.includes(guard.replace('|', '/'));
    });
  }

  /**
   * Set default data
   */
  resetData() {
    this.routes$.next([]);
    this.permitted_paths.next([]);
  }
}

/**
 * The notification count that is displayed on the sidebar
 */
export type SidebarNotifications = {
  /**
   * Each key is a route, and each value is an object with the key being the notification type, and the value being the count.
   */
  [key: string]: {
    [key: string]: number | object;
  };
};
